import { Button, Close } from '@bphxd/ds-core-react';
import { FC, useState } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { Form, FormGroup, Label, Input } from 'reactstrap';
import classes from '../JobFiltersModal/JobFiltersModal.module.scss';
import { EMPTY_ISOLATION_FILTERS } from '../../../lib/constants';
import Select from 'react-select';
import { ReactSelectOverrides } from '@bphxd/ds-core-react';
import { optionsFromEnum, stringToOption } from '../../../lib/util/format';
import {
  ISOLATION_DRAWING_STATUS_ENUM,
  TASK_WORK_CENTER_OPTIONS,
} from '../../../lib/constants/dbConstants';
import clsx from 'clsx';
import { Trash24 } from '@bphxd/ds-core-react/lib/icons';
import { TAspectsTableFilters } from '../AspectsTable/AspectsTable';
import {
  getEventOptions,
  getAugmentedOptionsFromMapping as getOptions,
  getIsolationOptions,
} from '../JobFiltersModal/JobFiltersModal.util';
import { useSelectedAsset } from '../../../lib/util';
import { useQuery } from '@apollo/client';
import { GET_EVENTS_BY_ASSET } from '../../../graph-queries/tar/event';
import { TEvent } from '../../../lib/types';
import { renderOptions } from '../../../lib/util/render';
import { REQUIRED_TRADE_OPTIONS } from '../IsolationForm/IsolationForm';

export type TIsolationFilters = TAspectsTableFilters<
  | 'ecowIsolation.statusComment'
  | 'isolationNumber'
  | 'fieldWalked'
  | 'drawingStatus'
  | 'event'
  | 'loshNumber'
  | 'requiredTrades'
>;
const fieldWalkOptions = [
  { value: 'YES', label: 'Yes' },
  { value: 'NO', label: 'No' },
];
const csvToOptions = (csvArray: string[] | null | undefined) => {
  if (!csvArray || csvArray.length === 0) return [];

  // Since it's always an array with one CSV string, split the first element
  const values = csvArray[0].split(',').map((v) => v.trim());

  return values
    .map((val) => REQUIRED_TRADE_OPTIONS.find((opt) => opt.value === val))
    .filter(Boolean) as { label: string; value: string }[];
};
const optionsToCsv = (options: { value: string }[]): string[] => {
  const csv = options.map((o) => o.value).join(',');
  return [csv]; // Wrap in array to match expected shape
};

const TIsolationFiltersModal: FC<{
  defaultValues: TIsolationFilters;
  onSubmit: (filters: TIsolationFilters) => unknown;
  onClose: () => void;
}> = ({ defaultValues, onSubmit, onClose }) => {
  const [filters, setFilters] = useState<TIsolationFilters>(defaultValues);

  const setFilterByKey = (key: keyof TIsolationFilters, values: string[]) => {
    setFilters((filters) => ({ ...filters, [key]: values }));
  };
  const handleSubmit = () => {
    onSubmit(filters);
    onClose();
  };

  const handleDelete = () => {
    onSubmit(EMPTY_ISOLATION_FILTERS);
    onClose();
  };
  const asset = useSelectedAsset();
  const { data, loading } = useQuery(GET_EVENTS_BY_ASSET, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });
  const events = data?.eventsByAssetId as TEvent[];

  return (
    <Modal isOpen toggle={onClose} className="modal-dialog-centered">
      <ModalHeader className="border-0 px-6" close={<Close onClick={onClose} />}>
        Filter Isolations
      </ModalHeader>
      <ModalBody className={clsx('p-6 pt-0', classes.root)}>
        <div className={classes.filters}>
          <Form className="row g-5">
            {/* <div className="col-md-6">MO Status</div> */}
            <div className="col-md-6">
              <FormGroup>
                <Label>Event</Label>
                <Select
                  defaultValue={filters.event.map(stringToOption)}
                  isMulti
                  options={getEventOptions(asset, events).map(stringToOption)}
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  components={ReactSelectOverrides.default}
                  classNamePrefix="react-select"
                  onChange={(newSelection) => {
                    setFilterByKey(
                      'event',
                      newSelection.map((o) => o.value)
                    );
                  }}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label>Isolation Status</Label>
                <Select
                  defaultValue={filters['ecowIsolation.statusComment'].map(stringToOption)}
                  isMulti
                  options={getIsolationOptions().map(stringToOption)}
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  components={ReactSelectOverrides.default}
                  classNamePrefix="react-select"
                  onChange={(newSelection) => {
                    setFilterByKey(
                      'ecowIsolation.statusComment',
                      newSelection.map((o) => o.value)
                    );
                  }}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label>Drawing Status</Label>
                <Select
                  defaultValue={filters.drawingStatus.map(stringToOption)}
                  isMulti
                  options={optionsFromEnum(ISOLATION_DRAWING_STATUS_ENUM)}
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  components={ReactSelectOverrides.default}
                  classNamePrefix="react-select"
                  onChange={(newSelection) => {
                    setFilterByKey(
                      'drawingStatus',
                      newSelection.map((o) => o.value)
                    );
                  }}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label>Field Walked</Label>
                <Select
                  defaultValue={fieldWalkOptions.find(
                    (option) => option.value === filters.fieldWalked[0]
                  )}
                  options={fieldWalkOptions}
                  hideSelectedOptions={false}
                  closeMenuOnSelect={true}
                  isMulti
                  components={ReactSelectOverrides.default}
                  classNamePrefix="react-select"
                  onChange={(newSelection) => {
                    setFilterByKey(
                      'fieldWalked',
                      newSelection.map((o) => o.value)
                    );
                  }}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label>Required Trades</Label>
                <Select
                  isMulti
                  defaultValue={csvToOptions(filters.requiredTrades)}
                  options={REQUIRED_TRADE_OPTIONS}
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  components={ReactSelectOverrides.default}
                  classNamePrefix="react-select"
                  onChange={(selectedOptions) => {
                    const mutableOptions = [...(selectedOptions || [])];
                    setFilterByKey('requiredTrades', optionsToCsv(mutableOptions));
                  }}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label>LOSH</Label>
                <Input
                  type="text"
                  title="Use comma to separate multiple values like '22,223'. Values need to be exact."
                  name="loshNumber"
                  id="losh-number"
                  defaultValue={filters.loshNumber}
                  onChange={(e) => {
                    const loshNumberArrary: string[] = e.target.value.split(',');
                    setFilterByKey('loshNumber', loshNumberArrary);
                  }}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label>Isolation Number</Label>
                <Input
                  type="text"
                  title="Use comma to separate multiple values like 'A123,B456'. Values need to be exact."
                  name="isolationNumber"
                  id="isolation-number"
                  defaultValue={filters.isolationNumber}
                  onChange={(e) => {
                    const isolationNumberArrary: string[] = e.target.value.split(',');
                    setFilterByKey('isolationNumber', isolationNumberArrary);
                  }}
                />
              </FormGroup>
            </div>
            {/* Footer */}
            <div className={clsx('col-md-12', classes.footer)}>
              <Button
                size="lg"
                className=""
                theme="darker"
                level="tertiary"
                onClick={handleDelete}
                Icon={Trash24}
              >
                Remove Filters
              </Button>
              <Button
                disabled={filters === defaultValues}
                size="md"
                className=""
                theme="darker"
                level="primary"
                onClick={handleSubmit}
              >
                Apply Filters
              </Button>
            </div>
          </Form>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default TIsolationFiltersModal;
